import React, { useMemo } from 'react'
import { trans } from 'sharedKernel/i18n'
import type { ScoutingOpportunity } from 'domain/feature/hub/company/scouting/api/scoutingService'
import {
  displayOrDash,
  formatOpportunityVerticalsDisplay,
  getOpportunitySupportFileDisplayName,
  getOpportunitySupportFiles,
  isExternalUrl,
  normalizeExternalUrl,
  type ScoutingOpportunityFile
} from 'domain/feature/hub/company/scouting/scoutingOpportunityDisplay'
import { ScoutingPdfFileIcon } from './ScoutingPdfFileIcon'
import {
  AboutRoot,
  MetaBar,
  MetaItem,
  MetaLabel,
  MetaValue,
  DescriptionCard,
  DescriptionHeader,
  DescriptionTitle,
  DescriptionDivider,
  DescriptionBody,
  DetailBlock,
  DetailLabel,
  DetailValue,
  DetailLink,
  SupportFilesList,
  SupportFileChip,
} from './styles'

interface ScoutingAboutProps {
  data: ScoutingOpportunity | null | undefined
  fileRows?: ScoutingOpportunityFile[] | null
}

export const ScoutingAbout = ({ data, fileRows }: ScoutingAboutProps): JSX.Element => {
  const apiSupportFiles = getOpportunitySupportFiles(fileRows, data)
  const hasApiSupportFiles = apiSupportFiles.length > 0
  const segmentDisplay = useMemo(() => formatOpportunityVerticalsDisplay(data), [data])

  const descriptionHtml = data?.description?.trim()
  const supportLink = data?.supportLink?.trim() ?? ''

  const renderSupportLink = (): JSX.Element => {
    if (!supportLink) {
      return <DetailValue>{displayOrDash()}</DetailValue>
    }

    if (!isExternalUrl(supportLink)) {
      return <DetailLink as="span">{supportLink}</DetailLink>
    }

    return (
      <DetailLink href={normalizeExternalUrl(supportLink)} target="_blank" rel="noopener noreferrer">
        {supportLink}
      </DetailLink>
    )
  }

  const renderSupportMaterials = (): JSX.Element => {
    if (hasApiSupportFiles) {
      return (
        <SupportFilesList>
          {apiSupportFiles.map((file, index) => {
            const displayName =
              getOpportunitySupportFileDisplayName(file, data?.title) ||
              trans('scouting.about.unnamedFile')

            return (
              <SupportFileChip
                key={file.id ?? `file-${index}`}
                href={file.path ?? '#'}
                target="_blank"
                rel="noopener noreferrer"
                download={displayName}
              >
                <ScoutingPdfFileIcon />
                <span>{displayName}</span>
              </SupportFileChip>
            )
          })}
        </SupportFilesList>
      )
    }

    return <DetailValue>-</DetailValue>
  }

  return (
    <AboutRoot>
      <MetaBar>
        <MetaItem>
          <MetaLabel>{trans('scouting.about.offeringInstitution')}:</MetaLabel>
          <MetaValue>{displayOrDash(data?.requesterName)}</MetaValue>
        </MetaItem>
        <MetaItem>
          <MetaLabel>{trans('scouting.about.segment')}:</MetaLabel>
          <MetaValue>{displayOrDash(segmentDisplay)}</MetaValue>
        </MetaItem>
        <MetaItem>
          <MetaLabel>{trans('scouting.about.impacted')}:</MetaLabel>
          <MetaValue>{displayOrDash(data?.impactedAudience)}</MetaValue>
        </MetaItem>
      </MetaBar>

      <DescriptionCard>
        <DescriptionHeader>
          <DescriptionTitle>{trans('scouting.about.descriptionTitle')}</DescriptionTitle>
          <DescriptionDivider />
        </DescriptionHeader>

        {descriptionHtml ? (
          <DescriptionBody dangerouslySetInnerHTML={{ __html: descriptionHtml }} />
        ) : (
          <DescriptionBody>{displayOrDash()}</DescriptionBody>
        )}

        <DetailBlock>
          <DetailLabel>{trans('scouting.about.solutionPaths')}:</DetailLabel>
          <DetailValue>{displayOrDash(data?.references)}</DetailValue>
        </DetailBlock>
        <DetailBlock>
          <DetailLabel>{trans('scouting.about.supportLink')}:</DetailLabel>
          {renderSupportLink()}
        </DetailBlock>
        <DetailBlock>
          <DetailLabel>{trans('scouting.about.supportMaterials')}</DetailLabel>
          {renderSupportMaterials()}
        </DetailBlock>
      </DescriptionCard>
    </AboutRoot>
  )
}
